/**
 * Option Pricing Calculator
 * 
 * Handles pricing calculations for individual adventure options including
 * dropdowns, checkboxes, text inputs, and number inputs with their various
 * pricing rules and multipliers.
 * 
 * @author Wildlife Project Team
 * @version 2.1.0
 */

import { type OptionType } from '../types';
import { type DropdownChoice } from '@/utils/businessAppApi';
import { 
  PriceBreakdownItem, 
  FormValues, 
  PricingRule 
} from './pricing-types';
import { 
  calculatePricingValue, 
  shouldIncludeInPricing, 
  generateBreakdownId 
} from './pricingUtils';

/**
 * Calculate pricing for dropdown options with individual choice pricing
 * 
 * @param option - The dropdown option configuration
 * @param selectedValue - Selected value(s) from the dropdown
 * @param scaledBasePrice - Current scaled base price for percentage calculations
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param legacyMultipliers - Legacy multiplier values for backward compatibility
 * @param currencySymbol - Currency symbol for descriptions
 * @returns Calculated amount for the dropdown selection
 */
export function calculateDropdownPricing(
  option: OptionType,
  selectedValue: any,
  scaledBasePrice: number,
  peopleValue: number,
  daysValue: number,
  legacyMultipliers: Record<string, number>,
  currencySymbol: string
): number {
  let totalAmount = 0;
  const selectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];

  for (const value of selectedValues) {
    // Check for enhanced dropdown with individual choice pricing
    if (Array.isArray(option.options)) {
      const dropdownChoice = option.options.find((choice: any) => 
        typeof choice === 'object' && choice.value === value
      ) as DropdownChoice | undefined;

      if (dropdownChoice?.pricing) {
        // Use individual choice pricing
        for (const pricing of dropdownChoice.pricing) {
          totalAmount += calculatePricingValue(
            pricing as PricingRule,
            scaledBasePrice,
            1,
            peopleValue,
            daysValue,
            legacyMultipliers[option.label] || 1
          );
        }
      } else if (option.pricing) {
        // Fallback to option-level pricing
        for (const pricing of option.pricing) {
          totalAmount += calculatePricingValue(
            pricing as PricingRule,
            scaledBasePrice,
            1,
            peopleValue,
            daysValue,
            legacyMultipliers[option.label] || 1
          );
        }
      }
    }
  }

  return totalAmount;
}

/**
 * Calculate pricing for checkbox options
 * 
 * @param option - The checkbox option configuration
 * @param isChecked - Whether the checkbox is checked
 * @param scaledBasePrice - Current scaled base price for percentage calculations
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param legacyMultipliers - Legacy multiplier values
 * @param currencySymbol - Currency symbol for descriptions
 * @returns Array of price breakdown items for the checkbox
 */
export function calculateCheckboxPricing(
  option: OptionType,
  isChecked: boolean,
  scaledBasePrice: number,
  peopleValue: number,
  daysValue: number,
  legacyMultipliers: Record<string, number>,
  currencySymbol: string
): PriceBreakdownItem[] {
  const breakdownItems: PriceBreakdownItem[] = [];

  if (!isChecked || !option.pricing || option.pricing.length === 0) {
    return breakdownItems;
  }

  for (const [priceIndex, price] of option.pricing.entries()) {
    let amount = 0;
    let description = price.description || '';

    switch (price.type) {
      case 'flat':
      case 'fixed':
        amount = price.value;
        description = description || `Added ${option.label}`;
        break;
        
      case 'percentage':
        amount = (scaledBasePrice * price.value) / 100;
        description = description || `${price.value}% for ${option.label}`;
        break;
        
      case 'per_unit':
        // Legacy per_unit with multiplierType (backward compatibility)
        if (price.multiplierType) {
          switch (price.multiplierType) {
            case 'people':
              amount = price.value * peopleValue;
              description = description || `${currencySymbol}${price.value} × ${peopleValue} ppl`;
              break;
            case 'days':
              amount = price.value * daysValue;
              description = description || `${currencySymbol}${price.value} × ${daysValue} days`;
              break;
            case 'both':
              amount = price.value * peopleValue * daysValue;
              description = description || `${currencySymbol}${price.value} × ${peopleValue} ppl × ${daysValue} days`;
              break;
            default:
              // Fallback to legacy multiplier system
              const legacyMultiplier = legacyMultipliers[option.label] || 1;
              amount = price.value * legacyMultiplier;
              description = description || `${currencySymbol}${price.value} × ${legacyMultiplier}`;
          }
        } else {
          // Standard per_unit without multiplier
          amount = price.value;
          description = description || `${option.label} charge`;
        }
        break;

      // NEW SIMPLIFIED PRICING TYPES
      case 'per_person':
        amount = price.value * peopleValue;
        description = description || `${currencySymbol}${price.value} × ${peopleValue} ppl`;
        break;
        
      case 'per_day':
        amount = price.value * daysValue;
        description = description || `${currencySymbol}${price.value} × ${daysValue} days`;
        break;
        
      case 'per_person_day':
        amount = price.value * peopleValue * daysValue;
        description = description || `${currencySymbol}${price.value} × ${peopleValue} ppl × ${daysValue} days`;
        break;
    }

    if (amount > 0) {
      breakdownItems.push({
        id: generateBreakdownId('checkbox', option.label, priceIndex),
        label: `${option.label}${priceIndex > 0 ? ` (${priceIndex + 1})` : ''}`,
        amount,
        description,
        type: 'option'
      });
    }
  }

  return breakdownItems;
}

/**
 * Calculate pricing for text and number input options
 * 
 * @param option - The input option configuration
 * @param value - Current input value
 * @param scaledBasePrice - Current scaled base price for percentage calculations
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param legacyMultipliers - Legacy multiplier values
 * @returns Array of price breakdown items for the input
 */
export function calculateInputPricing(
  option: OptionType,
  value: any,
  scaledBasePrice: number,
  peopleValue: number,
  daysValue: number,
  legacyMultipliers: Record<string, number>,
  currencySymbol: string = '€'
): PriceBreakdownItem[] {
  const breakdownItems: PriceBreakdownItem[] = [];

  if (!shouldIncludeInPricing(value, option.type) || !option.pricing) {
    return breakdownItems;
  }

  for (const [priceIndex, price] of option.pricing.entries()) {
    const amount = calculatePricingValue(
      price as PricingRule,
      scaledBasePrice,
      value,
      peopleValue,
      daysValue,
      legacyMultipliers[option.label] || 1
    );

    if (amount > 0) {
      let description = price.description || '';
      
      // Generate math formula if description is not provided
      if (!description) {
        switch (price.type) {
          case 'flat':
          case 'fixed':
            description = `${currencySymbol}${price.value}`;
            break;
          case 'percentage':
            description = `${price.value}% of base`;
            break;
          case 'per_unit':
            if (price.multiplierType) {
              switch (price.multiplierType) {
                case 'people':
                  description = `${currencySymbol}${price.value} × ${peopleValue} people`;
                  break;
                case 'days':
                  description = `${currencySymbol}${price.value} × ${daysValue} days`;
                  break;
                case 'both':
                  description = `${currencySymbol}${price.value} × ${peopleValue} people × ${daysValue} days`;
                  break;
                default:
                  const legacyMultiplier = legacyMultipliers[option.label] || 1;
                  description = `${currencySymbol}${price.value} × ${legacyMultiplier}`;
              }
            } else {
              const quantity = typeof value === 'number' ? value : 1;
              description = `${currencySymbol}${price.value} × ${quantity}`;
            }
            break;
          case 'per_person':
            description = `${currencySymbol}${price.value} × ${peopleValue} people`;
            break;
          case 'per_day':
            description = `${currencySymbol}${price.value} × ${daysValue} days`;
            break;
          case 'per_person_day':
            description = `${currencySymbol}${price.value} × ${peopleValue} people × ${daysValue} days`;
            break;
          default:
            description = `${option.label} charge`;
        }
      }
      
      breakdownItems.push({
        id: generateBreakdownId('input', option.label, priceIndex),
        label: option.label,
        amount,
        description,
        type: 'option'
      });
    }
  }

  return breakdownItems;
}

/**
 * Calculate pricing for all options in the adventure
 * 
 * @param options - Array of all adventure options
 * @param formValues - Current form values
 * @param scaledBasePrice - Current scaled base price
 * @param peopleValue - Number of people
 * @param daysValue - Number of days
 * @param legacyMultipliers - Legacy multiplier values
 * @param currencySymbol - Currency symbol for descriptions
 * @returns Object containing total amount and breakdown items
 */
export function calculateAllOptionsPricing(
  options: OptionType[],
  formValues: FormValues,
  scaledBasePrice: number,
  peopleValue: number,
  daysValue: number,
  legacyMultipliers: Record<string, number>,
  currencySymbol: string
): { totalAmount: number; breakdownItems: PriceBreakdownItem[] } {
  let totalAmount = 0;
  const breakdownItems: PriceBreakdownItem[] = [];

  for (const option of options) {
    const value = formValues[option.label];
    
    // CRITICAL FIX 1: Skip people/days number fields that are used as base price scaling factors
    // These should NOT add extra charges when base price already scales
    if (option.type === 'number' && isPeopleOrDaysScalingField(option.label)) {
      // Only add pricing if this field has explicit pricing AND it's not just a scaling factor
      if (option.pricing && option.pricing.length > 0) {
        // Check if this is genuinely meant to be an add-on cost vs just a scaling factor
        const hasNonScalingPricing = option.pricing.some(price => 
          !['per_person', 'per_day', 'per_person_day'].includes(price.type)
        );
        
        if (hasNonScalingPricing) {
          // Only include non-scaling pricing types for people/days fields
          const inputItems = calculateInputPricing(
            option,
            value,
            scaledBasePrice,
            peopleValue,
            daysValue,
            legacyMultipliers,
            currencySymbol
          ).filter(item => {
            // Filter out scaling-based pricing for people/days fields to prevent double-charging
            const optionPricing = option.pricing?.find((_, idx) => 
              item.id.includes(`-${idx}`)
            );
            return optionPricing && !['per_person', 'per_day', 'per_person_day'].includes(optionPricing.type);
          });
          
          inputItems.forEach(item => {
            totalAmount += item.amount;
            breakdownItems.push(item);
          });
        }
      }
      continue; // Skip further processing for scaling fields
    }
    
    // CRITICAL FIX 3: Better skip condition that handles dropdown choice pricing
    // Skip options without any pricing (option-level OR choice-level) and no values
    if (!hasAnyPricing(option)) continue;
    
    // Skip if there's no user input value (except for checkboxes which can be false)
    if (!value && option.type !== 'checkbox') continue;

    // CRITICAL FIX 2: Enhanced dropdown pricing calculation with proper breakdown
    if ((option.type === 'select' || option.type === 'dropdown') && option.options && value) {
      const dropdownAmount = calculateDropdownPricing(
        option,
        value,
        scaledBasePrice,
        peopleValue,
        daysValue,
        legacyMultipliers,
        currencySymbol
      );
      
      if (dropdownAmount > 0) {
        totalAmount += dropdownAmount;
        
        // IMPROVED: Create detailed breakdown for dropdown choices
        const selectedChoice = findSelectedDropdownChoice(option, value);
        const choiceLabel = selectedChoice ? 
          (typeof selectedChoice === 'object' ? selectedChoice.value : selectedChoice) : 
          value;
          
        const choicePricingDetails = getDropdownChoicePricingDetails(
          option, 
          value, 
          peopleValue, 
          daysValue, 
          currencySymbol
        );
        
        breakdownItems.push({
          id: generateBreakdownId('dropdown', option.label),
          label: `${option.label}: ${choiceLabel}`,
          amount: dropdownAmount,
          description: choicePricingDetails || `Selected: ${choiceLabel}`,
          type: 'choice'
        });
      }
    }

    // Handle checkbox pricing
    if (option.type === 'checkbox') {
      const checkboxItems = calculateCheckboxPricing(
        option,
        value === true,
        scaledBasePrice,
        peopleValue,
        daysValue,
        legacyMultipliers,
        currencySymbol
      );
      
      checkboxItems.forEach(item => {
        totalAmount += item.amount;
        breakdownItems.push(item);
      });
    }

    // Handle other input types (text, non-scaling number fields)
    if (['text'].includes(option.type) || 
        (option.type === 'number' && !isPeopleOrDaysScalingField(option.label))) {
      const inputItems = calculateInputPricing(
        option,
        value,
        scaledBasePrice,
        peopleValue,
        daysValue,
        legacyMultipliers,
        currencySymbol
      );
      
      inputItems.forEach(item => {
        totalAmount += item.amount;
        breakdownItems.push(item);
      });
    }
  }

  return { totalAmount, breakdownItems };
}

/**
 * HELPER FUNCTIONS FOR FIXES
 */

/**
 * Check if an option has any pricing (option-level OR individual choice-level)
 * This fixes the critical dropdown pricing bug where options with only choice-level
 * pricing were being skipped
 */
function hasAnyPricing(option: OptionType): boolean {
  // Check option-level pricing
  if (option.pricing && option.pricing.length > 0) {
    return true;
  }
  
  // Check individual choice pricing for dropdown options
  if ((option.type === 'select' || option.type === 'dropdown') && Array.isArray(option.options)) {
    return option.options.some((choice: any) => 
      typeof choice === 'object' && choice.pricing && choice.pricing.length > 0
    );
  }
  
  return false;
}

/**
 * Check if a field is used for base price scaling (people/days)
 */
function isPeopleOrDaysScalingField(label: string): boolean {
  const lowerLabel = label.toLowerCase();
  return lowerLabel.includes('people') || 
         lowerLabel.includes('person') || 
         lowerLabel.includes('day') || 
         lowerLabel.includes('days') || 
         lowerLabel.includes('night') || 
         lowerLabel.includes('nights');
}

/**
 * Find the selected dropdown choice object
 */
function findSelectedDropdownChoice(option: OptionType, selectedValue: any): any {
  if (!Array.isArray(option.options)) return null;
  
  return option.options.find((choice: any) => {
    if (typeof choice === 'string') return choice === selectedValue;
    if (typeof choice === 'object' && choice.value) return choice.value === selectedValue;
    return false;
  });
}

/**
 * Get detailed pricing information for dropdown choice
 */
function getDropdownChoicePricingDetails(
  option: OptionType, 
  selectedValue: any, 
  peopleValue: number, 
  daysValue: number, 
  currencySymbol: string
): string {
  const selectedChoice = findSelectedDropdownChoice(option, selectedValue);
  
  if (selectedChoice && typeof selectedChoice === 'object' && selectedChoice.pricing) {
    const pricingTexts = selectedChoice.pricing.map((pricing: any) => {
      switch (pricing.type) {
        case 'flat':
        case 'fixed':
          return `${currencySymbol}${pricing.value}`;
        case 'per_person':
          return `${currencySymbol}${pricing.value} × ${peopleValue} ppl`;
        case 'per_day':
          return `${currencySymbol}${pricing.value} × ${daysValue} days`;
        case 'per_person_day':
          return `${currencySymbol}${pricing.value} × ${peopleValue} ppl × ${daysValue} days`;
        case 'percentage':
          return `${pricing.value}% of base`;
        default:
          return `${currencySymbol}${pricing.value}`;
      }
    });
    
    return pricingTexts.join(', ');
  }
  
  return '';
} 